import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Copy, ArrowRightLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Base64Tool() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("encode");
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [urlSafe, setUrlSafe] = useState(false);
  const [stats, setStats] = useState(null);

  const handleEncode = async () => {
    if (!input.trim()) {
      toast.error("Please enter text to encode");
      return;
    }
    try {
      const response = await axios.post(`${API}/base64/encode`, {
        text: input,
        url_safe: urlSafe
      });
      setOutput(response.data.encoded);
      setStats({
        originalSize: response.data.original_size,
        encodedSize: response.data.encoded_size,
        increase: response.data.size_increase_percent
      });
      toast.success("Text encoded successfully!");
    } catch (error) {
      toast.error("Failed to encode");
    }
  };

  const handleDecode = async () => {
    if (!input.trim()) {
      toast.error("Please enter base64 to decode");
      return;
    }
    try {
      const response = await axios.post(`${API}/base64/decode`, {
        encoded: input,
        url_safe: urlSafe
      });
      setOutput(response.data.decoded);
      setStats(null);
      toast.success("Text decoded successfully!");
    } catch (error) {
      toast.error("Invalid base64 string");
    }
  };

  const handleProcess = () => {
    if (mode === "encode") {
      handleEncode();
    } else {
      handleDecode();
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const swapInputOutput = () => {
    const temp = input;
    setInput(output);
    setOutput(temp);
    setMode(mode === "encode" ? "decode" : "encode");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0b] via-[#0f0f12] to-[#0a0a0b]">
      {/* Header */}
      <div className="border-b border-zinc-800/50 backdrop-blur-xl bg-zinc-900/30">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center space-x-4">
            <Button
              data-testid="back-home-btn"
              variant="ghost"
              size="sm"
              onClick={() => navigate("/")}
              className="text-zinc-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-2xl font-bold text-white" style={{fontFamily: 'Space Grotesk'}}>Base64 Tool</h1>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Mode Selector */}
        <Card className="bg-zinc-900/50 border-zinc-800 p-6 mb-6">
          <Tabs value={mode} onValueChange={setMode} className="w-full">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 bg-zinc-800 border-zinc-700">
              <TabsTrigger value="encode" data-testid="encode-tab">Encode</TabsTrigger>
              <TabsTrigger value="decode" data-testid="decode-tab">Decode</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="flex items-center justify-center space-x-4 mt-6">
            <div className="flex items-center space-x-2">
              <Switch
                data-testid="url-safe-switch"
                checked={urlSafe}
                onCheckedChange={setUrlSafe}
                id="url-safe"
              />
              <Label htmlFor="url-safe" className="text-zinc-400 cursor-pointer">
                URL-Safe Base64
              </Label>
            </div>
          </div>
        </Card>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Input */}
          <Card className="bg-zinc-900/50 border-zinc-800 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white" style={{fontFamily: 'Space Grotesk'}}>
                {mode === "encode" ? "Plain Text" : "Base64 String"}
              </h3>
              {input && (
                <Button
                  data-testid="clear-input-btn"
                  variant="ghost"
                  size="sm"
                  onClick={() => setInput("")}
                  className="text-zinc-400 hover:text-white"
                >
                  Clear
                </Button>
              )}
            </div>
            <Textarea
              data-testid="input-textarea"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={mode === "encode" ? "Enter text to encode..." : "Enter base64 to decode..."}
              className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm min-h-[400px]"
            />
            {input && (
              <div className="mt-4 text-sm text-zinc-500">
                Length: {input.length} characters
              </div>
            )}
          </Card>

          {/* Output */}
          <Card className="bg-zinc-900/50 border-zinc-800 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white" style={{fontFamily: 'Space Grotesk'}}>
                {mode === "encode" ? "Base64 String" : "Plain Text"}
              </h3>
              {output && (
                <Button
                  data-testid="copy-output-btn"
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(output)}
                  className="text-zinc-400 hover:text-white"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy
                </Button>
              )}
            </div>
            <Textarea
              data-testid="output-textarea"
              value={output}
              readOnly
              placeholder="Result will appear here..."
              className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm min-h-[400px]"
            />
            {output && (
              <div className="mt-4 text-sm text-zinc-500">
                Length: {output.length} characters
              </div>
            )}
          </Card>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-center space-x-4 mt-6">
          <Button
            data-testid="process-btn"
            onClick={handleProcess}
            disabled={!input.trim()}
            className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white px-8"
          >
            {mode === "encode" ? "Encode" : "Decode"}
          </Button>
          {output && (
            <Button
              data-testid="swap-btn"
              onClick={swapInputOutput}
              variant="outline"
              className="border-zinc-700 hover:bg-zinc-800"
            >
              <ArrowRightLeft className="w-4 h-4 mr-2" />
              Swap
            </Button>
          )}
        </div>

        {/* Stats */}
        {stats && (
          <Card className="bg-zinc-900/50 border-zinc-800 p-6 mt-6">
            <h3 className="text-lg font-bold text-white mb-4" style={{fontFamily: 'Space Grotesk'}}>Size Analysis</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <div className="text-sm text-zinc-400 mb-1">Original Size</div>
                <div className="text-2xl font-bold text-white">{stats.originalSize} bytes</div>
              </div>
              <div>
                <div className="text-sm text-zinc-400 mb-1">Encoded Size</div>
                <div className="text-2xl font-bold text-white">{stats.encodedSize} bytes</div>
              </div>
              <div>
                <div className="text-sm text-zinc-400 mb-1">Size Increase</div>
                <div className="text-2xl font-bold text-cyan-400">+{stats.increase}%</div>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}